/* Nordic Pet House — actual Shopify allocations, native cart form, no recurring-payment emulation. */
(function () {
  'use strict';
  const INTERVALS = Object.freeze([7, 14, 21, 28, 42, 56]);
  const positive = value => {
    const text = String(value ?? '').trim().replace(',', '.');
    return /^\d+(?:\.\d+)?$/.test(text) && Number(text) > 0 && Number.isFinite(Number(text)) ? Number(text) : null;
  };
  const unit = value => ({g:'g',gram:'g',grams:'g',kg:'kg',kilogram:'kg',kilograms:'kg',l:'l',liter:'l',liters:'l',litre:'l',litres:'l',units:'units'})[String(value || '').trim().toLowerCase()] || null;
  function measure(text) {
    const s = String(text || '').toLowerCase();
    // Never interpret "6 x 2 kg" as a single sack. Such products are outside this feature's scope.
    if (/\d\s*[x×]\s*\d/.test(s)) return null;
    const matches = [...s.matchAll(/(?:^|[^\d.,])(\d+(?:[.,]\d+)?)\s*(kilograms?|kg|grams?|g|litres?|liters?|l)(?![a-zåäö])/g)];
    if (matches.length !== 1) return null;
    return {amount: positive(matches[0][1]), unit: unit(matches[0][2])};
  }
  function calculatorType(product) {
    if (['food', 'cat_litter'].includes(product.calculatorType)) return product.calculatorType;
    const classify = value => {
      const s = String(value || '').toLowerCase().replace(/[_-]+/g, ' ');
      if (/kattsand|cat\s*litter/.test(s)) return 'cat_litter';
      if (/hundfoder|kattfoder|torrfoder|dog\s*food|cat\s*food|dry\s*food|kibble|^(food)$/.test(s)) return 'food';
      return null;
    };
    return classify(product.type) || classify((product.tags || []).join(' ')) || classify(product.title) || 'manual';
  }
  function pack(product, variant, type = calculatorType(product)) {
    const net = {amount:positive(variant.netWeight?.value),unit:unit(variant.netWeight?.unit)};
    const meta = {amount:positive(variant.packAmount),unit:unit(variant.packUnit)};
    const named = measure(variant.title), titled = product.singleSize ? measure(product.title) : null;
    const weight = positive(variant.weightGrams);
    const normalize = p => {
      if (!p?.amount || !p.unit || (type === 'food' && !['kg','g'].includes(p.unit))) return null;
      return type === 'cat_litter' && p.unit === 'g' ? {amount:p.amount / 1000,unit:'kg'} : p;
    };
    if (type === 'food') return normalize(net) || (weight ? {amount:weight / 1000,unit:'kg'} : null) || normalize(named) || normalize(titled);
    if (type === 'cat_litter') {
      const knownMeta = normalize(meta); if (knownMeta) return knownMeta;
      // Litres describe net volume. The shipping weight must never replace that volume.
      const volumeSold = meta.unit === 'l' || named?.unit === 'l' || /\d\s*(?:l|liter|litre)\b/i.test(product.title || '');
      if (volumeSold) return normalize(named?.unit === 'l' ? named : null) || normalize(titled?.unit === 'l' ? titled : null);
      return (weight ? {amount:weight / 1000,unit:'kg'} : null) || normalize(named) || normalize(titled);
    }
    return normalize(meta); // Unknown product types require an explicit amount/unit, not guessed shipping weight.
  }
  function parseInterval(plan) {
    const parse = value => {
      const s=String(value || '').trim().toLowerCase();
      if (/mån|month|year|år/.test(s)) return null; // Calendar months are not four-week periods.
      let m=s.match(/(?:^|[^\d])(\d+)(?::[ea])?\s*(?:days?|dagar?|dygn|weeks?|veckor|vecka)(?![a-zåäö])/);
      if(m) {
        const days=Number(m[1]) * (/week|veck/.test(m[0]) ? 7 : 1);
        return INTERVALS.includes(days) ? days : null;
      }
      const words=[[/varje vecka|every week|weekly/,7],[/varannan vecka|every (?:two|other) weeks?/,14],[/var tredje vecka|every three weeks/,21],[/var fjärde vecka|every four weeks/,28],[/var sjätte vecka|every six weeks/,42],[/var åttonde vecka|every eight weeks/,56]];
      return words.find(([re])=>re.test(s))?.[1] || null;
    };
    const delivery=(plan.options || []).filter(o=>/deliver|leverans|ship|frekvens|frequency/i.test(o.name || ''));
    const candidates=delivery.length ? delivery : (plan.options || []).filter(o=>!(/bill|betal|discount|rabatt/i.test(o.name || '')));
    const days=[...new Set(candidates.map(o=>parse(o.value)).filter(Boolean))];
    if (days.length === 1) return days[0];
    if (delivery.length || days.length > 1) return null;
    return parse(plan.name);
  }
  function allocations(variant) {
    return (variant?.allocations || []).filter(a=>a.plan?.recurring === true && a.plan.id && Number.isFinite(a.perDelivery) && a.perDelivery >= 0).map(a=>({...a,days:parseInterval(a.plan)}));
  }
  function calculate({type, pack: p, quantity, usage, intervals}) {
    const amount=positive(p?.amount), qty=positive(quantity), used=positive(usage);
    if (!amount || !qty || !used || !p?.unit) return null;
    if (type === 'food' && !['g','kg'].includes(p.unit)) return null;
    const net=type === 'food' ? amount * (p.unit === 'kg' ? 1000 : 1) : amount;
    const days=net * qty / used * (type === 'food' ? 1 : 7);
    if (!Number.isFinite(days)) return null;
    const available=[...new Set(intervals)].filter(n=>INTERVALS.includes(n)).sort((a,b)=>a-b);
    const safeDays=days-2;
    const recommendation=available.filter(n=>n<=safeDays+1e-9).at(-1) || available[0] || null;
    return {days,safeDays,recommendation,tooShort:available.length>0 && safeDays<available[0],longSupply:available.length>0 && days>available.at(-1)};
  }
  const Core=Object.freeze({INTERVALS,positive,unit,measure,calculatorType,pack,parseInterval,allocations,calculate});
  if (typeof module !== 'undefined' && module.exports) module.exports=Core;
  if (typeof window === 'undefined' || !window.customElements) return;
  window.NPHSubscriptionsCore=Core;
  if (customElements.get('nph-subscription')) return;
  const handovers=new Map(); // One synchronous native re-render only; never browser storage.
  class Subscription extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.form=this.closest('form');
      if (!this.form || this.form.querySelector('nph-subscription') !== this) { this.hidden=true; return; }
      try { this.data=JSON.parse(this.querySelector('[data-subscription-data]').content.textContent); } catch (_) { return; }
      this.controller=new AbortController(); const signal=this.controller.signal;
      const handover=handovers.get(this.dataset.formId);
      const restored=handover?.productId===this.data.productId ? handover : null;
      this.mode=restored?.mode || 'one-time';this.manual=false;this.manualInterval=false;
      this.field=this.querySelector('[data-selling-plan]');this.field.disabled=false;
      this.form.addEventListener('product:rerender',e=>{
        if(!e.detail?.productChange){
          handovers.set(this.dataset.formId,{productId:this.data.productId,mode:this.mode,quantity:this.quantity(),usage:this.q('[data-usage]').value,type:this.type,unit:this.packValue?.unit,days:this.selected()?.days,planId:this.selected()?.plan.id,manualInterval:this.manualInterval,calculatorOpen:this.q('details').open});
          queueMicrotask(()=>handovers.delete(this.dataset.formId));
        }
        const findWidget=root=>{
          if(!root?.querySelectorAll)return null;
          const direct=[...root.querySelectorAll('nph-subscription')].find(el=>el.dataset.formId===this.dataset.formId);
          if(direct)return direct;
          for(const template of root.querySelectorAll('template')){const match=findWidget(template.content);if(match)return match;}
          return null;
        };
        const fresh=findWidget(e.detail?.htmlFragment);
        if(fresh) { try { this.pendingData=JSON.parse(fresh.querySelector('[data-subscription-data]').content.textContent); } catch (_) { this.pendingData=null; } }
      },{capture:true,signal});
      this.form.addEventListener('variant:change',e=>{
        if(this.pendingData) { this.data=this.pendingData;this.pendingData=null; }
        // The matching native section HTML is authoritative; never reuse a previous variant's plan.
        if(String(this.data.variant?.id)!==String(e.detail?.variant?.id)) this.data.variant={id:e.detail?.variant?.id,price:e.detail?.variant?.price,required:e.detail?.variant?.requires_selling_plan,available:e.detail?.variant?.available,allocations:[]};
        this.setVariant(true);
      },{signal});
      document.addEventListener('change',e=>{
        if(e.target?.form===this.form && e.target.name==='quantity') this.refresh(true);
      },{signal});
      document.addEventListener('input',e=>{
        if(e.target?.form===this.form && e.target.name==='quantity') this.refresh(true);
      },{signal});
      this.addEventListener('change',e=>{
        if(e.target.matches('[data-purchase]')) { this.mode=e.target.value; this.refresh(false); }
        if(e.target.matches('[data-interval]')) { this.manualInterval=true;this.refresh(false); }
        if(e.target.matches('[data-pack-unit]')) {
          this.q('[data-usage]').value=''; // No automatic mass/volume or consumption-unit conversion.
          this.refresh(true);
        }
      },{signal});
      this.addEventListener('input',e=>{ if(e.target.matches('[data-usage],[data-pack-amount]'))this.refresh(true); },{signal});
      this.addEventListener('click',e=>{
        if(e.target.closest('[data-edit-pack]')) {this.manual=true;this.configurePack();this.q('[data-pack-amount]').focus();this.refresh(true);}
        if(e.target.closest('[data-reset-pack]')) {this.manual=false;this.configurePack();this.refresh(true);}
        if(e.target.closest('[data-apply-recommendation]')) {this.manualInterval=false;this.refresh(true);}
      },{signal});
      // Validate before the native Ajax submit listener constructs FormData.
      this.form.addEventListener('submit',e=>{
        this.refresh(false);
        const id=this.form.elements.namedItem('id')?.value;
        if(this.variant.price<=500 || this.mode==='subscription' && (!this.selected() || String(id)!==String(this.data.variant?.id)) || this.data.variant?.required && !this.field.value) {
          e.preventDefault(); e.stopImmediatePropagation();this.text('[data-sub-status]',this.t('subscription_unavailable'));this.q('[data-interval]').focus();
        }
      },{capture:true,signal});
      this.relocateQuantity();
      if(restored?.quantity){
        const quantity=this.form.elements.namedItem('quantity');
        if(quantity){const selector=quantity.closest('quantity-selector');if(selector && 'quantity' in selector)selector.quantity=restored.quantity;else quantity.value=String(restored.quantity);}
      }
      this.setVariant(false);
      if(restored){
        if(restored.type===this.type && restored.unit===this.packValue?.unit)this.q('[data-usage]').value=restored.usage;
        const selection=this.plans.find(a=>a.plan.id===restored.planId) || this.plans.find(a=>a.days && a.days===restored.days);
        if(selection)this.q('[data-interval]').value=String(selection.plan.id);
        this.manualInterval=!!restored.manualInterval;this.q('details').open=restored.calculatorOpen;this.refresh(true);
      }
    }
    disconnectedCallback() { this.controller?.abort();this.controller=null; }
    q(selector) { return this.querySelector(selector); }
    text(selector,value) { const el=this.q(selector); if(el && el.textContent!==String(value))el.textContent=String(value); }
    t(key,values={}) {return Object.entries(values).reduce((s,[k,v])=>s.replaceAll('['+k+']',String(v)),this.data.messages[key] || key);}
    number(value) { return new Intl.NumberFormat(this.data.locale || 'sv',{maximumFractionDigits:1}).format(value); }
    money(value) { return new Intl.NumberFormat(this.data.locale || 'sv',{style:'currency',currency:this.data.currency || 'SEK'}).format(value / 100); }
    label(a) { return a?.days ? this.t('week_'+a.days/7) : a?.plan.name || ''; }
    unitLabel(u) { return this.t('unit_'+u); }
    quantity() { return positive(this.form.elements.namedItem('quantity')?.value ?? 1); }
    relocateQuantity() {
      // Keep the original quantity widget and native re-render identity, physically inside this product form.
      const input=this.form.elements.namedItem('quantity');
      const block=input?.closest('[data-block-type="quantity-selector"]');
      if(block && !this.form.contains(block)) this.before(block);
    }
    setVariant(changed) {
      const previousType=this.type,previousUnit=this.packValue?.unit,previous=this.selected();
      this.type=calculatorType(this.data);
      this.variant=this.data.variant || {};this.plans=allocations(this.variant);
      this.detected=pack(this.data,this.variant,this.type);this.manual=false;
      this.configurePack();
      if(changed && (previousType!==this.type || previousUnit!==this.packValue?.unit))this.q('[data-usage]').value='';
      const select=this.q('[data-interval]');select.replaceChildren();
      const duplicateDays=new Set(this.plans.filter((a,i,all)=>a.days && all.some((b,j)=>i!==j && b.days===a.days)).map(a=>a.days));
      this.plans.forEach(a=>{
        const option=document.createElement('option');option.value=String(a.plan.id);
        option.textContent=a.days ? this.t('interval_days',{interval:this.label(a),days:a.days}) : a.plan.name;
        if(duplicateDays.has(a.days)) option.textContent+=' — '+a.plan.name;
        select.append(option);
      });
      const matching=this.plans.find(a=>String(a.plan.id)===String(previous?.plan.id)) || this.plans.find(a=>a.days && a.days===previous?.days);
      if(matching) select.value=String(matching.plan.id);
      this.manualInterval=changed ? false : this.manualInterval;
      this.hidden=this.plans.length===0 || this.variant.price<=500;
      if(this.hidden)this.mode='one-time';
      else if(this.variant.required)this.mode='subscription';
      this.q('[data-one-card]').hidden=!!this.variant.required;
      this.q('[data-purchase][value="one-time"]').disabled=!!this.variant.required;
      this.refresh(true);
      if(changed)this.text('[data-sub-status]',this.t(this.hidden?'no_plan':'plan_changed'));
    }
    configurePack() {
      this.packValue=this.detected;
      const manual=!this.detected || this.manual;
      this.q('[data-manual-pack]').hidden=!manual;
      this.q('[data-edit-pack]').hidden=manual;
      this.q('[data-reset-pack]').hidden=!this.detected;
      this.q('[data-feeding]').hidden=this.type!=='food';
      const allowed=this.type==='food' ? ['g','kg'] : this.type==='cat_litter' ? ['kg','l'] : ['units','kg','l'];
      const select=this.q('[data-pack-unit]');
      [...select.options].forEach(o=>{o.hidden=!allowed.includes(o.value);o.disabled=o.hidden;});
      select.value=this.detected?.unit || allowed[0];
      this.q('[data-pack-amount]').value=this.detected?.amount ?? '';
      this.text('[data-pack-found]',this.detected?this.t('pack_found',{amount:this.number(this.detected.amount),unit:this.unitLabel(this.detected.unit)}):this.t('manual_info'));
    }
    currentPack() {
      return !this.detected || this.manual ? {amount:positive(this.q('[data-pack-amount]').value),unit:this.q('[data-pack-unit]').value} : this.detected;
    }
    selected() { return this.plans?.find(a=>String(a.plan.id)===this.q('[data-interval]').value); }
    refresh(autoRecommend) {
      const qty=this.quantity(),p=this.currentPack();this.packValue=p;
      const used=this.q('[data-usage]').value;
      const usageUnit=this.type==='food'?'grams_day':p?.unit==='l'?'liters_week':p?.unit==='units'?'units_week':'kg_week';
      this.text('[data-usage-label]',this.t(this.type==='food'?'food_usage':this.type==='cat_litter'?'litter_usage':'manual_usage'));
      this.text('[data-usage-unit]',this.t(usageUnit));
      const result=calculate({type:this.type,pack:p,quantity:qty,usage:used,intervals:this.plans.map(a=>a.days)});
      if(result?.recommendation && autoRecommend && !this.manualInterval) {
        const option=this.plans.find(a=>a.days===result.recommendation);
        // Preserve the selected plan if multiple plans share the recommended interval.
        if(this.selected()?.days!==result.recommendation)this.q('[data-interval]').value=String(option.plan.id);
      }
      const a=this.selected(),sub=this.mode==='subscription' && !!a && !this.hidden;
      this.field.value=sub?String(a.plan.id):'';
      this.querySelectorAll('[data-purchase]').forEach(r=>{r.checked=r.value===(sub?'subscription':'one-time');});
      this.q('[data-sub-panel]').hidden=!sub;
      this.q('[data-interval]').disabled=!sub;
      this.text('[data-selected-label]',this.t(sub?'selected':'subscribe_help'));
      this.text('[data-one-price]',this.money((this.variant.price || 0)*(qty || 1)));
      if(a) {
        const price=a.perDelivery*(qty || 1),compare=a.compareAt*(qty || 1);
        this.text('[data-sub-price]',this.money(price));this.text('[data-sub-compare]',this.money(compare));
        this.q('[data-sub-compare]').hidden=!(compare>price);
        this.q('[data-sub-compare]').setAttribute('aria-label',this.t('regular_price')+': '+this.money(compare));
        this.q('[data-discount]').hidden=!(compare>price);
        this.text('[data-discount]',this.t('discount',{percent:this.number((1-price/compare)*100)}));
        this.text('[data-quantity-price]',this.t('quantity_price',{quantity:qty || 1}));
        this.q('[data-charge]').hidden=a.checkout===a.perDelivery || a.checkout==null;
        this.text('[data-charge]',this.t('checkout_charge',{price:this.money(a.checkout*(qty || 1))}));
        this.q('[data-remaining]').hidden=!(a.remaining>0);
        this.text('[data-remaining]',this.t('later_charge',{price:this.money(a.remaining*(qty || 1))}));
        const next=a.adjustments?.[1],orders=a.plan.adjustments?.[0]?.order_count;
        this.q('[data-future-price]').hidden=!next || !orders;
        if(next && orders)this.text('[data-future-price]',this.t('future_price',{orders,price:this.money(next.price*(qty || 1))}));
        this.q('[data-plan-description]').hidden=!a.plan.description;
        this.text('[data-plan-description]',a.plan.description || '');
        this.text('[data-recurring]',this.t('recurring',{interval:this.label(a),price:this.money(price)}));
      }
      this.q('[data-result]').hidden=!result;
      this.q('[data-calc-prompt]').hidden=!!result;
      this.text('[data-calc-prompt]',this.t(used ? 'invalid':'start_calc'));
      this.q('[data-warning]').hidden=true;
      if(result) {
        this.text('[data-result-pack]',this.number(p.amount)+' '+this.unitLabel(p.unit));
        this.text('[data-result-quantity]',this.number(qty));
        this.text('[data-result-usage]',this.number(positive(used))+' '+this.t(usageUnit));
        this.text('[data-result-days]',this.t('days',{days:this.number(result.days)}));
        this.text('[data-result-interval]',this.label(a));
        this.text('[data-recommendation]',result.recommendation?this.t('recommended',{interval:this.t('week_'+result.recommendation/7)}):this.t('no_recommendation'));
        const override=result.recommendation && a?.days!==result.recommendation;
        this.q('[data-override]').hidden=!override;this.q('[data-apply-recommendation]').hidden=!override;
        this.q('[data-long-supply]').hidden=!result.longSupply;
        const short=result.tooShort || (a?.days && a.days>result.safeDays);
        this.q('[data-warning]').hidden=!short && !!a?.days;
        this.text('[data-warning]',this.t(short?this.type==='food'?'food_warning':this.type==='cat_litter'?'litter_warning':'manual_warning':'unknown_interval'));
      }
      const missingRequiredPlan=!!this.variant.required && !a;
      const blocked=this.variant.price<=500 || missingRequiredPlan;
      const unavailable=this.form.querySelector('[data-subscription-unavailable]');if(unavailable)unavailable.hidden=!missingRequiredPlan;
      [...this.form.elements].filter(el=>el.type==='submit').forEach(button=>{
        if(blocked && !button.disabled){button.disabled=true;button.dataset.nphSubBlocked='true';}
        else if(!blocked && button.dataset.nphSubBlocked){button.disabled=!this.variant.available;delete button.dataset.nphSubBlocked;}
      });
      const payment=this.form.querySelector('.shopify-payment-button');
      if(payment)payment.classList.toggle('nph-subscription-payment-hidden',sub && !this.data.dynamicCheckout || this.variant.required && !a);
      this.q('[data-payment-note]').hidden=!sub || !!this.data.dynamicCheckout;
      this.updateSticky(sub,a,qty);
      this.form.dispatchEvent(new CustomEvent('nph:purchase-option-change',{bubbles:true,detail:{variantId:this.variant.id,sellingPlanId:sub?a.plan.id:null,quantity:qty,perDeliveryPrice:sub?a.perDelivery:null}}));
    }
    updateSticky(sub,a,qty) {
      // Sticky actions submit this very form. Show its recurring total instead of a one-time price there.
      document.querySelectorAll('product-sticky-bar').forEach(bar=>{
        if(bar.getAttribute('form')!==this.form.getAttribute('id'))return;
        const nativePrice=bar.querySelector('price-list');
        let summary=bar.querySelector('.nph-sub-sticky-summary');
        if(!summary){summary=document.createElement('span');summary.className='nph-sub-sticky-summary';nativePrice?.after(summary);}
        if(nativePrice){nativePrice.hidden=sub;nativePrice.classList.toggle('nph-subscription-payment-hidden',sub);}
        summary.hidden=!sub;
        if(sub)summary.textContent=this.money(a.perDelivery*(qty || 1))+' '+this.t('per_delivery')+' · '+this.label(a);
      });
    }
  }
  customElements.define('nph-subscription',Subscription);
})();
